/**
 * How tall a FULL-PAGE grid is allowed to get before its own body starts scrolling.
 *
 * **Why any bound exists.** `<main>` is the app's only scroll container: the shell root
 * is `h-screen overflow-clip` and the document itself never scrolls
 * (`Components/Templates/Layout.tsx`). An unbounded table therefore makes MAIN the thing
 * that scrolls, which drags the page title, the search field, the header buttons and the
 * pager off-screen along with the rows. At per-page 10 nobody notices; at 50 or 100 the
 * whole chrome leaves. Reported live on `/gms/guide`, then on `/gms/compatibilities`.
 *
 * **Why a shared constant rather than a number per page.** Every one of these grids sits
 * in the same shell under the same page grammar — title row, divider, grid, pager — so
 * they reserve almost exactly the same space. Measured live, chrome above + below the
 * grid inside `<main>`, plus the 60px navbar outside it:
 *
 * | page | reserve |
 * |---|---|
 * | `/gms/lists` | 177 |
 * | `/gms/guide` | 181 |
 * | `/gms/compatibilities` | 185 |
 *
 * 200 covers all three with 15–23px of slack, so a control growing a size rung does not
 * reopen the bug. **Tune this constant, not the call sites** — that is the whole point of
 * it being one name. A page whose chrome genuinely differs passes its own value instead.
 *
 * **The slack is deliberate and must not be trimmed to zero.** The header rows on these
 * pages hold controls whose height comes from the control ladder; a rung change there
 * moves every one of these numbers at once.
 */
export const PAGE_GRID_MAX_HEIGHT = 'calc(100vh - 200px)';

/**
 * The same bound for a grid that sits below a page header **and** a tab strip — today the
 * ISR/Guide model builder's inner tabs (Relationships, UI Model).
 *
 * Measured on the Relationships tab: 166px of chrome above the grid against a top-level
 * list page's ~80px, because the model header and `ISRTabs` both sit above it. Total
 * reserve 270; 290 carries the same 20px of slack as its sibling above.
 *
 * Two rungs, not a continuum: a grid is either directly under a page header or under a
 * tab strip as well. If a third shape appears, measure it and add a third name here
 * rather than inlining a literal at the call site.
 */
export const TABBED_GRID_MAX_HEIGHT = 'calc(100vh - 290px)';
